import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PhoneCall,
  Loader2,
  CheckCircle2,
  XCircle,
  PhoneOff,
  Mic,
  IndianRupee,
  CalendarClock,
  FileText,
  Briefcase,
  Code2,
  ThumbsUp,
  RefreshCw,
  Download,
  Lock,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Input } from '@/Components/ui/input';
import { API_BASE_URL } from '@/config/api';
import { PlanLockCard, InsufficientCreditsCard } from '../../Components/PlanFeatureLock';

/**
 * VoiceScreenPopup
 *
 * Triggers and tracks an AI telephonic screening call (Exotel + realtime
 * voice agent). The recruiter first picks a mode:
 *   - hr        : first-round HR screen — interest, current/expected CTC,
 *                 notice period, and a few light role questions (~3-4 min).
 *   - technical : ~15-min deep technical screen — the agent asks strong,
 *                 role-specific technical questions generated from the JD's
 *                 required skills, and the answers are scored on depth.
 *
 * Backend: POST /api/voice-screening/call {mode} to dial, then this popup
 * polls GET /api/voice-screening/calls until the call reaches a terminal
 * state and the post-call evaluation lands.
 */

const ACTIVE_STATUSES = ['initiated', 'in_call'];

const STATUS_LABELS = {
  initiated: 'Dialing…',
  in_call: 'On call',
  completed: 'Completed',
  failed: 'Failed',
  busy: 'Busy',
  'no-answer': 'No answer',
  error: 'Error',
};

const STATUS_TONES = {
  initiated: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  in_call: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  failed: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
  busy: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
  'no-answer': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  error: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
};

// The two screening modes the recruiter chooses between.
const MODES = [
  {
    key: 'hr',
    icon: Briefcase,
    label: 'HR screening',
    blurb: 'Interest, current & expected CTC, notice period, a few role questions',
    duration: '~3–4 min',
  },
  {
    key: 'technical',
    icon: Code2,
    label: 'Technical screening',
    blurb: 'In-depth role questions from the job description (any field), scored on depth',
    duration: '~15 min',
  },
];

// Recommendation pill styling for technical results.
const RECOMMENDATION_TONES = {
  strong_yes: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  yes: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  maybe: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  no: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
};

// Backend timestamps are naive UTC (no tz suffix). Append 'Z' so the browser
// converts to the viewer's local time instead of treating UTC as local.
function parseUtc(ts) {
  if (!ts) return null;
  const iso = /[Zz]|[+-]\d\d:?\d\d$/.test(ts) ? ts : `${ts}Z`;
  return new Date(iso);
}

function formatTs(ts) {
  const d = parseUtc(ts);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// End reasons that legitimately produce NO score (nothing was screened) — so
// the popup must NOT sit on "Scoring…" or keep polling for them.
const NO_SCORE_REASONS = ['voicemail', 'wrong_person'];

// True only while we're genuinely waiting for the post-call evaluation: the
// call completed WITH a real conversation, no summary yet, and it hasn't been
// stuck too long (guards against a Gemini failure spinning forever).
function isScoring(call) {
  if (!call || call.status !== 'completed' || call.summary) return false;
  if (NO_SCORE_REASONS.includes(call.end_reason)) return false;
  const hasTranscript = Array.isArray(call.transcript) && call.transcript.length > 0;
  if (!hasTranscript) return false;
  const ended = parseUtc(call.ended_at);
  if (ended && Date.now() - ended.getTime() > 4 * 60 * 1000) return false;
  return true;
}

export default function VoiceScreenPopup({
  open,
  candidate = {},
  jobId = null,
  initiatedBy = null,
  onClose = () => {},
}) {
  const email = (candidate.email || candidate.candidate_email || '').toLowerCase();
  const resolvedJobId = jobId || candidate.job_id || null;
  // Company-side identity, same fallback chain the candidate card uses
  // elsewhere for admin/employer actions.
  const actor =
    initiatedBy ||
    sessionStorage.getItem('admin_email') ||
    localStorage.getItem('admin_email') ||
    sessionStorage.getItem('employer_email') ||
    localStorage.getItem('employer_email') ||
    null;

  const [phone, setPhone] = useState('');
  const [mode, setMode] = useState('hr'); // 'hr' | 'technical' — for the NEXT call
  const [starting, setStarting] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [error, setError] = useState('');
  const [call, setCall] = useState(null); // latest call row from the backend
  const [loaded, setLoaded] = useState(false);
  // Master kill-switch state (super-admin Services tab). Defaults true so the
  // button works if the health check is slow/unavailable — the backend gate is
  // the real enforcement; this is just UX so the button reads as off cleanly.
  const [featureEnabled, setFeatureEnabled] = useState(true);
  // Plan entitlements: feature lock (ai_phone_screening) + fixed per-call
  // credit costs (PHONE_SCREEN_HR / PHONE_SCREEN_TECHNICAL) + balance.
  const [ent, setEnt] = useState(null);
  // Custom monthly phone allowance (only set for contracts like Balio).
  const phoneAllowance = (ent?.monthly_limits?.items || []).find((i) => i.key === 'phone_screen_units') || null;
  const phoneAllowanceRenews = ent?.monthly_limits?.renews_on || '';
  const phoneAllowanceSupport = ent?.monthly_limits?.support_email || 'contact@searchjob.ai';
  const pollRef = useRef(null);

  const fetchLatest = useCallback(async () => {
    if (!resolvedJobId || !email) return null;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/voice-screening/calls?job_id=${resolvedJobId}` +
          `&candidate_email=${encodeURIComponent(email)}&limit=1`,
        { credentials: 'include' },
      );
      if (!res.ok) return null;
      const rows = await res.json();
      const latest = Array.isArray(rows) && rows.length ? rows[0] : null;
      setCall(latest);
      return latest;
    } catch (e) {
      return null;
    } finally {
      setLoaded(true);
    }
  }, [resolvedJobId, email]);

  // Reset + load the latest call whenever the popup opens. Also read the
  // feature kill-switch from the public health endpoint so a disabled feature
  // shows a clean note instead of failing only when the button is clicked.
  useEffect(() => {
    if (!open) return;
    setError('');
    setStarting(false);
    setLoaded(false);
    setMode('hr');
    setPhone(candidate.phone || '');
    fetchLatest();
    fetch(`${API_BASE_URL}/api/voice-screening/health`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        // Only force-disable on an explicit false — fail open otherwise.
        if (data && data.feature_enabled === false) setFeatureEnabled(false);
        else setFeatureEnabled(true);
      })
      .catch(() => setFeatureEnabled(true));

    // Plan lock + fixed per-call cost + balance. Resolve by job (backend maps
    // it to the owning company), falling back to the stored company id.
    const storedCid = localStorage.getItem('companyAccountId');
    const entQuery = resolvedJobId
      ? `job_id=${encodeURIComponent(resolvedJobId)}`
      : (storedCid ? `company_id=${encodeURIComponent(storedCid)}` : '');
    if (entQuery) {
      fetch(`${API_BASE_URL}/api/entitlements/me?${entQuery}`, { credentials: 'include' })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => setEnt(data || null))
        .catch(() => setEnt(null));
    }
  }, [open, candidate, fetchLatest, resolvedJobId]);

  // Poll while a call is live, or genuinely awaiting its score. A voicemail /
  // wrong-number call has no transcript to score, so isScoring() is false and
  // we stop polling instead of spinning on "Scoring…" forever.
  useEffect(() => {
    if (!open) return undefined;
    const needsPolling =
      call && (ACTIVE_STATUSES.includes(call.status) || isScoring(call));
    if (!needsPolling) return undefined;
    pollRef.current = setInterval(fetchLatest, 4000);
    return () => clearInterval(pollRef.current);
  }, [open, call, fetchLatest]);

  // Re-run the evaluation for a call whose scoring failed. The transcript is
  // already saved, so this is cheap and safe to retry.
  const rescore = async () => {
    if (!call?.id) return;
    setError('');
    setRescoring(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/voice-screening/calls/${call.id}/rescore`,
        { method: 'POST', credentials: 'include' },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.error || `Scoring failed (HTTP ${res.status}).`);
      } else if (body.call) {
        setCall(body.call);
        if (!body.scored) {
          setError('Scoring ran but produced no result — check the server logs.');
        }
      }
    } catch (e) {
      setError('Could not reach the server to re-score.');
    } finally {
      setRescoring(false);
    }
  };

  // A plain link can't carry the sign-in token, so fetch the PDF and save it.
  const downloadReport = async () => {
    if (!call?.id || downloadingReport) return;
    setError('');
    setDownloadingReport(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/voice-screening/calls/${call.id}/report.pdf`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || `Could not download the report (HTTP ${res.status}).`);
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const slug = String(call.candidate_email || 'candidate').split('@')[0].replace(/[^a-zA-Z0-9_-]/g, '-');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${call.mode || 'hr'}-screening_${slug}_${call.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError('Could not reach the server to download the report.');
    } finally {
      setDownloadingReport(false);
    }
  };

  const startCall = async () => {
    setError('');
    setStarting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/voice-screening/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_id: resolvedJobId,
          candidate_email: email,
          phone: phone || undefined,
          mode,
          initiated_by: actor || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409 && body.call) {
        setCall(body.call); // a call is already running — just track it
      } else if (res.status === 403 && body.upgrade_required) {
        setError(`AI phone screening isn't included in your ${ent?.plan_name || 'current'} plan. Upgrade to use it.`);
      } else if (res.status === 402) {
        setError(`Not enough credits${body.required ? ` (needs ${body.required})` : ''}. Please top up.`);
      } else if (!res.ok) {
        setError(body.error || `Could not start the call (HTTP ${res.status})`);
      }
      await fetchLatest();
    } catch (e) {
      setError('Could not reach the server. Is the backend running?');
    } finally {
      setStarting(false);
    }
  };

  const isActive = call && ACTIVE_STATUSES.includes(call.status);
  const isScoringPending = isScoring(call);

  // Plan lock + fixed per-call credit costs (HR = 1, Technical = 3 by default).
  const enforced = !!ent?.enforced;
  const planLocked = enforced && ent?.features?.ai_phone_screening === false;
  const hrCost = Number(ent?.test_costs?.PHONE_SCREEN_HR ?? 1);
  const techCost = Number(ent?.test_costs?.PHONE_SCREEN_TECHNICAL ?? 3);
  const currentCost = mode === 'technical' ? techCost : hrCost;
  const balance = Number(ent?.balance ?? 0);
  const canAfford = !enforced || balance >= currentCost;

  const canStart =
    !starting && !isActive && featureEnabled && !planLocked && canAfford &&
    resolvedJobId && email;

  const evaluation = call?.evaluation || {};
  // The call's own mode (what was actually run), separate from `mode` which is
  // the selection for the NEXT call.
  const callMode = call?.mode === 'technical' ? 'technical' : 'hr';
  const isTechnicalCall = callMode === 'technical';
  // Both modes store a question list — technical under `questions`, HR under
  // `role_questions`. Pick whichever this call produced.
  const questionList = Array.isArray(evaluation.questions)
    ? evaluation.questions
    : Array.isArray(evaluation.role_questions)
    ? evaluation.role_questions
    : [];
  const recommendation = evaluation.recommendation || null;
  const transcript = Array.isArray(call?.transcript) ? call.transcript : [];

  // A future auto-call is queued for this candidate (one pending at a time).
  const pendingAutoAt = call?.callback_at ? parseUtc(call.callback_at) : null;
  const hasPendingAutoCall = !!(pendingAutoAt && pendingAutoAt.getTime() > Date.now());

  // Outcomes that trigger (or attempted) an auto-retry — surfaced in one banner.
  const isVoicemail = call?.end_reason === 'voicemail';
  const retryReason = !call
    ? null
    : call.end_reason === 'callback_requested'
    ? 'Candidate asked to be called back'
    : isVoicemail
    ? 'Reached voicemail'
    : call.status === 'no-answer'
    ? 'No answer'
    : call.status === 'busy'
    ? 'Line was busy'
    : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-4 w-4" />
            AI phone screen
          </DialogTitle>
          <DialogDescription className="text-xs">
            {candidate.name || email}
            {candidate.jobTitle ? ` — ${candidate.jobTitle}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Screening mode selector */}
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Screening type
            </div>
            <div className="grid grid-cols-2 gap-2">
              {MODES.map((m) => {
                const Icon = m.icon;
                const selected = mode === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMode(m.key)}
                    disabled={isActive || starting}
                    className={`flex flex-col items-start gap-1 rounded-md border p-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      selected
                        ? 'border-primary bg-primary/5'
                        : 'border-border/40 hover:bg-muted/50'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="h-3.5 w-3.5" />
                      {m.label}
                    </span>
                    <span className="text-[11px] leading-snug text-muted-foreground">
                      {m.blurb}
                    </span>
                    <span className="flex w-full items-center justify-between">
                      <span className="text-[10px] font-medium text-muted-foreground/80">
                        {m.duration}
                      </span>
                      <span className="text-[10px] font-semibold text-primary">
                        {(() => {
                          const c = m.key === 'technical' ? techCost : hrCost;
                          return `${c} ${c === 1 ? 'credit' : 'credits'}`;
                        })()}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Phone confirmation */}
          <div className="rounded-md border border-border/40 bg-card p-3">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Call this number
            </div>
            <Input
              className="h-8 text-sm"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d+\-\s()]/g, ''))}
              placeholder="+91 98765 43210"
              disabled={isActive || starting}
            />
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {mode === 'technical'
                ? 'Indian mobile numbers only. An expert AI interviewer asks in-depth questions drawn from this job description — whatever the field — and scores the answers on depth. Takes about 15 minutes.'
                : 'Indian mobile numbers only. The AI recruiter confirms interest in the role, asks current CTC, expected CTC, notice period, and 2–3 short role questions. Takes about 3–4 minutes.'}
            </p>
          </div>

          {!featureEnabled && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              AI phone screening is turned off for your organization. A super-admin
              can enable it under Services in the admin panel.
            </div>
          )}

          {/* Plan lock: the company's plan doesn't include AI phone screening. */}
          {planLocked && (
            <PlanLockCard
              featureKey="ai_phone_screening"
              featureLabel="AI phone screening"
              planName={ent?.plan_name}
            />
          )}

          {/* Enough plan access, but not enough credits for this call. */}
          {!planLocked && featureEnabled && !canAfford && (
            <InsufficientCreditsCard
              featureLabel={`An AI ${mode === 'technical' ? 'technical' : 'HR'} screening call`}
              cost={currentCost}
              balance={balance}
            />
          )}

          {/* Custom contract (e.g. Balio): dedicated-number note + this month's
              5-minute slot usage. Only shown when the company has a phone limit. */}
          {phoneAllowance && (
            <div className="rounded-md border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
              <p>
                AI phone screening for your account will run on your company&apos;s dedicated
                virtual number. Our team will be in touch to complete this setup. For any
                assistance, please write to{' '}
                <a href={`mailto:${phoneAllowanceSupport}`} className="font-medium underline">{phoneAllowanceSupport}</a>.
              </p>
              <p className="mt-1.5 font-medium">
                {phoneAllowance.used} of {phoneAllowance.limit} screening slots (5 minutes each) used this month
                {phoneAllowanceRenews ? ` · renews ${phoneAllowanceRenews}` : ''}.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          {/* Latest call status / results */}
          {!loaded ? (
            <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Checking call history…
            </div>
          ) : call ? (
            <div className="rounded-md border border-border/40 bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 normal-case">
                    {isTechnicalCall ? (
                      <><Code2 className="h-2.5 w-2.5" /> Technical</>
                    ) : (
                      <><Briefcase className="h-2.5 w-2.5" /> HR</>
                    )}
                  </span>
                  <span>
                    Latest call
                    {call.attempt > 1 ? ` · attempt ${call.attempt}` : ''}
                    {call.created_at ? ` — ${formatTs(call.created_at)}` : ''}
                  </span>
                </div>
                <Badge
                  variant="outline"
                  className={`border-0 px-2 py-0.5 text-[10px] font-medium uppercase ${
                    isVoicemail && !isScoringPending
                      ? STATUS_TONES['no-answer']
                      : STATUS_TONES[call.status] || STATUS_TONES['no-answer']
                  }`}
                >
                  {(isActive || isScoringPending) && (
                    <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" />
                  )}
                  {isScoringPending
                    ? 'Scoring…'
                    : isVoicemail
                    ? 'Voicemail'
                    : STATUS_LABELS[call.status] || call.status}
                </Badge>
              </div>

              {isActive && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {call.status === 'initiated'
                    ? 'Phone is ringing — waiting for the candidate to pick up.'
                    : isTechnicalCall
                    ? 'The AI interviewer is running the technical screen. Results appear here right after the call.'
                    : 'The AI recruiter is talking to the candidate. Results appear here right after the call.'}
                </p>
              )}

              {call.status === 'error' && call.error && (
                <p className="mt-2 break-words text-xs text-rose-600 dark:text-rose-400">
                  {call.error}
                </p>
              )}

              {/* Auto-retry outcomes: callback request, voicemail, no-answer, busy */}
              {retryReason && (
                <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                  <CalendarClock className="mt-0.5 h-3 w-3 flex-shrink-0" />
                  <span>
                    {retryReason}
                    {call.end_reason === 'callback_requested' && call.callback_note
                      ? `: “${call.callback_note}”`
                      : '.'}
                    {call.callback_at
                      ? ` Auto-retry scheduled for ${formatTs(call.callback_at)}.`
                      : ' Retry limit reached — call again manually when ready.'}
                  </span>
                </div>
              )}

              {/* Results, once scored */}
              {call.summary && (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* HR: interest signal. Technical: hiring recommendation. */}
                    {!isTechnicalCall && call.interested === true && (
                      <Badge className="border-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                        <CheckCircle2 className="mr-1 h-3 w-3" /> Interested
                      </Badge>
                    )}
                    {!isTechnicalCall && call.interested === false && (
                      <Badge className="border-0 bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400">
                        <XCircle className="mr-1 h-3 w-3" /> Not interested
                      </Badge>
                    )}
                    {isTechnicalCall && recommendation && (
                      <Badge
                        className={`border-0 ${
                          RECOMMENDATION_TONES[recommendation] || RECOMMENDATION_TONES.maybe
                        }`}
                      >
                        <ThumbsUp className="mr-1 h-3 w-3" />
                        {String(recommendation).replace(/_/g, ' ')}
                      </Badge>
                    )}
                    {call.overall_score != null && (
                      <Badge variant="outline" className="text-xs">
                        {isTechnicalCall ? 'Technical' : 'Score'}: {Math.round(call.overall_score)}/100
                      </Badge>
                    )}
                  </div>

                  {/* HR-only facts. Technical screens don't collect CTC/notice. */}
                  {!isTechnicalCall && (
                    <div className="grid grid-cols-3 gap-2">
                      <Fact icon={IndianRupee} label="Current CTC" value={call.current_ctc} />
                      <Fact icon={IndianRupee} label="Expected CTC" value={call.expected_ctc} />
                      <Fact icon={CalendarClock} label="Notice period" value={call.notice_period} />
                    </div>
                  )}

                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {call.summary}
                  </p>

                  {questionList.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {isTechnicalCall ? 'Technical questions' : 'Role questions'}
                      </div>
                      {questionList.map((q, i) => (
                        <div
                          key={i}
                          className="rounded border border-border/40 p-2 text-xs"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="font-medium">{q.question}</span>
                            {q.score != null && (
                              <Badge variant="outline" className="shrink-0 text-[10px]">
                                {q.score}/10
                              </Badge>
                            )}
                          </div>
                          {q.topic && (
                            <div className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground/70">
                              {q.topic}
                            </div>
                          )}
                          {q.answer_summary && (
                            <p className="mt-1 text-muted-foreground">{q.answer_summary}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Technical: surface demonstrated skills + gaps when present. */}
                  {isTechnicalCall &&
                    (Array.isArray(evaluation.skills_demonstrated) ||
                      Array.isArray(evaluation.skill_gaps)) && (
                      <div className="grid grid-cols-2 gap-2">
                        {Array.isArray(evaluation.skills_demonstrated) &&
                          evaluation.skills_demonstrated.length > 0 && (
                            <div className="rounded border border-border/40 p-2">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                                Strengths
                              </div>
                              <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                                {evaluation.skills_demonstrated.map((s, i) => (
                                  <li key={i}>• {s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        {Array.isArray(evaluation.skill_gaps) &&
                          evaluation.skill_gaps.length > 0 && (
                            <div className="rounded border border-border/40 p-2">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                                Gaps
                              </div>
                              <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                                {evaluation.skill_gaps.map((s, i) => (
                                  <li key={i}>• {s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                    )}

                </div>
              )}

              {/* Recording + transcript live OUTSIDE the scored-results block:
                  a call that ran but whose scoring failed must still show them. */}
              {(call.recording_url || transcript.length > 0) && (
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                  {/* Print-ready report — the whole screening in one PDF. */}
                  <button
                    type="button"
                    onClick={downloadReport}
                    disabled={downloadingReport}
                    className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline disabled:opacity-60"
                  >
                    {downloadingReport
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Download className="h-3 w-3" />} Download report
                  </button>
                  {call.recording_url && (
                    <a
                      href={call.recording_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
                    >
                      <Mic className="h-3 w-3" /> Recording
                    </a>
                  )}
                  {transcript.length > 0 && (
                    <details className="w-full">
                      <summary className="inline-flex cursor-pointer items-center gap-1 text-primary">
                        <FileText className="h-3 w-3" /> Transcript ({transcript.length} lines)
                      </summary>
                      <div className="mt-2 max-h-44 space-y-1 overflow-y-auto rounded border border-border/40 p-2">
                        {transcript.map((line, i) => (
                          <p key={i} className="text-[11px] leading-snug">
                            <span className="font-semibold">
                              {line.role === 'agent' ? 'AI' : 'Candidate'}:
                            </span>{' '}
                            {line.text}
                          </p>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Scoring produced nothing — say so, and offer a one-click retry
                  (the transcript is saved, so re-scoring is cheap and safe). */}
              {transcript.length > 0 && !call.summary && !isScoringPending && !isActive && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <p className="text-[11px] text-amber-700 dark:text-amber-400">
                    Automatic scoring didn't complete for this call — the transcript
                    above is still available.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    onClick={rescore}
                    disabled={rescoring}
                  >
                    {rescoring ? (
                      <>
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Scoring…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-1 h-3 w-3" /> Retry scoring
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <p className="px-1 text-xs text-muted-foreground">
              No screening calls yet for this candidate.
            </p>
          )}

          {/* Heads-up: a manual call now replaces the queued auto-call. */}
          {hasPendingAutoCall && !isActive && featureEnabled && (
            <p className="px-1 text-[11px] text-muted-foreground">
              <CalendarClock className="mr-1 inline h-3 w-3" />
              An auto-call is scheduled for {formatTs(call.callback_at)}. Starting
              a call now will replace it.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={startCall}
            disabled={!canStart}
            title={planLocked ? `Not included in your ${ent?.plan_name || 'current'} plan` : undefined}
          >
            {starting ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : isActive ? (
              <PhoneOff className="mr-1.5 h-3.5 w-3.5" />
            ) : planLocked ? (
              <Lock className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <PhoneCall className="mr-1.5 h-3.5 w-3.5" />
            )}
            {isActive
              ? 'Call in progress'
              : planLocked
              ? 'Not in your plan'
              : `Start ${mode === 'technical' ? 'technical' : 'HR'} call · ${currentCost} cr`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="rounded border border-border/40 p-2">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="mt-0.5 truncate text-xs font-medium" title={value || ''}>
        {value || '—'}
      </div>
    </div>
  );
}
